import React, { useState, useEffect } from "react";
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";
import { toast } from "react-toastify";
import { PREDEFINED_SCALES } from "./GradeScalesManager";
import { fetchAssessmentById } from "../../../utils/createAssessment";

interface SubmissionData {
  id: string;
  student_id: string;
  assignment_id: string;
  status: string;
  submitted_at: string;
  grade?: string;
  grade_percentage?: number;
  teacher_feedback?: string;
  teacher_grade?: string;
  teacher_grade_percentage?: number;
  student: {
    fullname: string;
    firstname: string;
    lastname: string;
  };
  assignment: {
    id: string;
    assessment_name: string;
    assessment_id: string;
    type: string;
    standard: string;
    due_date: string;
    user_id: string;
  };
}

interface AssessmentData {
  title: string;
  questions: any[];
  sections?: any[];
}

const SubmissionReviewPage = () => {
  const { submissionId } = useParams();
  const { user } = useUserStore();
  const navigate = useNavigate();
  const profile = useUserStore((state) => state.profile);
  const { fetchUserProfile } = useUserStore();

  const [searchParams] = useSearchParams();
  const ltiToken = searchParams.get("ltik");

  if (!profile && user) {
    fetchUserProfile(user);
  }

  const role = profile?.role;

  useEffect(() => {
    if (user && profile && !["teacher", "admin", "superadmin"].includes(role)) {
      toast.error("Access denied. You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [user, navigate]);

  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [assessment, setAssessment] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingFeedback, setSendingFeedback] = useState(false);

  // Grade adjustment state
  const [selectedGrade, setSelectedGrade] = useState<string>("");
  const [gradePercentage, setGradePercentage] = useState<number>(0);
  const [availableGrades, setAvailableGrades] = useState<string[]>([]);
  const [teacherFeedback, setTeacherFeedback] = useState<string>("");

  // Assessment results state
  const [answers, setAnswers] = useState<any>({});
  const [evaluations, setEvaluations] = useState<any>({});
  const [overview, setOverview] = useState<{
    total: number;
    correct: number;
    incorrect: number;
    percent: number;
  } | null>(null);

  useEffect(() => {
    if (submissionId && user?.id) {
      fetchSubmissionData();
    }
  }, [submissionId, user?.id]);

  // Load available grades when submission data is loaded
  useEffect(() => {
    if (submission?.student_id) {
      loadAvailableGrades();
    }
  }, [submission?.student_id]);

  const fetchSubmissionData = async () => {
    setLoading(true);
    try {
      // Fetch submission data first to get the IDs we need
      const { data: submissionData, error: submissionError } = await supabase
        .from("student_assignments")
        .select(
          `
          id,
          student_id,
          assignment_id,
          status,
          submitted_at,
          grade,
          grade_percentage,
          teacher_feedback
        `
        )
        .eq("id", submissionId)
        .maybeSingle();

      if (submissionError || !submissionData) {
        toast.error("Submission not found or access denied");
        return;
      }

      // Use Promise.all to fetch assignment and student profile in parallel
      const [assignmentResult, studentResult] = await Promise.all([
        supabase
          .from("assignments")
          .select(
            `
            id,
            assessment_name,
            assessment_id,
            type,
            standard,
            due_date,
            user_id
          `
          )
          .eq("id", submissionData.assignment_id)
          .maybeSingle(),

        supabase
          .from("profiles")
          .select(
            `
            id,
            firstname,
            lastname
          `
          )
          .eq("id", submissionData.student_id)
          .maybeSingle(),
      ]);

      const { data: assignmentData, error: assignmentError } = assignmentResult;
      const { data: studentProfile, error: profileError } = studentResult;

      if (assignmentError || !assignmentData) {
        toast.error("Assignment not found");
        return;
      }

      if (profileError || !studentProfile) {
        toast.error("Student profile not found");
        return;
      }

      // Transform the data
      const transformedSubmission: SubmissionData = {
        id: submissionData.id,
        student_id: submissionData.student_id,
        assignment_id: submissionData.assignment_id,
        status: submissionData.status,
        submitted_at: submissionData.submitted_at,
        grade: submissionData.grade,
        grade_percentage: submissionData.grade_percentage,
        teacher_feedback: submissionData.teacher_feedback,
        student: {
          fullname: `${studentProfile.firstname} ${studentProfile.lastname}`,
          firstname: studentProfile.firstname,
          lastname: studentProfile.lastname,
        },
        assignment: assignmentData,
      };

      setSubmission(transformedSubmission);

      // Fetch assessment data and results
      await fetchAssessmentData(transformedSubmission);

      // Initialize grade adjustment state
      if (transformedSubmission.teacher_grade) {
        setSelectedGrade(transformedSubmission.teacher_grade);
        setGradePercentage(transformedSubmission.teacher_grade_percentage || 0);
      } else {
        setSelectedGrade(transformedSubmission.grade || "");
        setGradePercentage(transformedSubmission.grade_percentage || 0);
      }

      if (transformedSubmission.teacher_feedback) {
        setTeacherFeedback(transformedSubmission.teacher_feedback);
      }
    } catch (error) {
      console.error("Error fetching submission:", error);
      toast.error("Failed to load submission data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentData = async (submissionData: SubmissionData) => {
    try {
      const { type, assessment_name } = submissionData.assignment;

      // Fetch the assessment result based on type
      let resultData = null;
      let assessmentData = null;

      switch (type) {
        case "quiz":
          const { data: quizResult } = await supabase
            .from("quizzes")
            .select("*")
            .eq("user_id", submissionData.student_id)
            .eq("quiz_id", submissionData.assignment.assessment_id)
            .eq("assignment_id", submissionData.assignment.id)
            .single();

          if (quizResult) {
            resultData = quizResult.quiz_json;
            assessmentData = await fetchRemoteAssessment(quizResult.quiz_id);
          }
          break;

        case "midterm":
          const { data: midtermResult } = await supabase
            .from("midterms")
            .select("*")
            .eq("user_id", submissionData.student_id)
            .eq("midterm_id", submissionData.assignment.assessment_id)
            .eq("assignment_id", submissionData.assignment.id)
            .single();

          if (midtermResult) {
            resultData = midtermResult.midterm_json;
            assessmentData = await fetchRemoteAssessment(
              midtermResult.midterm_id
            );
          }
          break;

        case "final":
          const { data: finalResult } = await supabase
            .from("finals")
            .select("*")
            .eq("user_id", submissionData.student_id)
            .eq("final_id", submissionData.assignment.assessment_id)
            .eq("assignment_id", submissionData.assignment.id)
            .single();

          if (finalResult) {
            resultData = finalResult.final_json;
            assessmentData = await fetchRemoteAssessment(finalResult.final_id);
          }
          break;

        case "project":
          const { data: projectResult } = await supabase
            .from("projects")
            .select("*")
            .eq("user_id", submissionData.student_id)
            .eq("project_id", submissionData.assignment.assessment_id)
            .eq("assignment_id", submissionData.assignment.id)
            .single();

          if (projectResult) {
            resultData = projectResult.project_json;
            assessmentData = await fetchRemoteAssessment(
              projectResult.project_id
            );
          }
          break;
      }

      if (resultData && assessmentData) {
        setAnswers(resultData.answers || {});
        setEvaluations(resultData.evaluations || {});
        setAssessment(assessmentData);

        // Calculate overview
        calculateOverview(
          assessmentData,
          resultData.answers || {},
          resultData.evaluations || {}
        );
      }
    } catch (error) {
      console.error("Error fetching assessment data:", error);
    }
  };

  const fetchRemoteAssessment = async (assessmentId: string) => {
    try {
      const { data: assessment, error } = await fetchAssessmentById(
        assessmentId
      );
      if (!error && assessment) {
        return assessment.assessment_json[assessmentId];
      }
    } catch (error) {
      console.error("Error fetching remote assessment:", error);
    }
    return null;
  };

  const calculateOverview = (
    assessmentData: AssessmentData,
    answers: any,
    evaluations: any
  ) => {
    let total = 0;
    let correct = 0;
    let incorrect = 0;

    if (assessmentData.sections) {
      // Handle sectioned assessments (midterms, finals, projects)
      assessmentData.sections.forEach((section: any, sectionIndex: number) => {
        section.questions.forEach((question: any, qIndex: number) => {
          total++;
          const answerKey = `${sectionIndex}-${qIndex}`;
          const evaluation = evaluations[answerKey];
          const userAnswer = answers[answerKey];

          // Enhanced correctness check
          const isCorrect = checkAnswerCorrectness(
            question,
            userAnswer,
            evaluation
          );

          if (isCorrect) {
            correct++;
          } else {
            incorrect++;
          }
        });
      });
    } else {
      // Handle flat assessments (quizzes)
      assessmentData.questions.forEach((question: any, index: number) => {
        total++;
        const evaluation = evaluations[index];
        const userAnswer = answers[index];

        // Enhanced correctness check
        const isCorrect = checkAnswerCorrectness(
          question,
          userAnswer,
          evaluation
        );

        if (isCorrect) {
          correct++;
        } else {
          incorrect++;
        }
      });
    }

    const percent = total > 0 ? Math.round((correct / total) * 100) : 0;

    setOverview({
      total,
      correct,
      incorrect,
      percent,
    });
  };

  // Helper function to check if an answer is correct
  const checkAnswerCorrectness = (
    question: any,
    userAnswer: any,
    evaluation: any
  ) => {
    // First, check if we have an explicit evaluation
    if (evaluation) {
      // Check for 'passed' property (used by ST and FB questions)
      if (typeof evaluation.passed === "boolean") {
        return evaluation.passed;
      }
    }

    // Handle FB questions specifically
    if (
      question.type === "FB" &&
      Array.isArray(question.correct) &&
      Array.isArray(userAnswer)
    ) {
      // Check each blank individually
      const isCorrect = question.correct.every(
        (correctVal: any, idx: number) => {
          // Get the accepted variants for this specific blank
          let acceptedVariants = [String(correctVal).toLowerCase()];

          // Handle accept_also variants for this specific blank
          if (
            question.accept_also &&
            Array.isArray(question.accept_also[idx])
          ) {
            acceptedVariants.push(
              ...question.accept_also[idx].map((variant: any) =>
                String(variant).toLowerCase().trim()
              )
            );
          } else if (
            question.accept_also &&
            typeof question.accept_also[idx] === "string"
          ) {
            acceptedVariants.push(
              String(question.accept_also[idx]).toLowerCase().trim()
            );
          }

          const userVal = String(userAnswer[idx] || "")
            .toLowerCase()
            .trim();

          return acceptedVariants.includes(userVal);
        }
      );

      return isCorrect;
    }

    // Handle MC questions
    if (question.type === "MC" && question.correct && userAnswer) {
      let accepted = [String(question.correct).toLowerCase()];

      // Handle accept_also for MC questions
      if (question.accept_also && Array.isArray(question.accept_also)) {
        accepted.push(
          ...question.accept_also.map((variant: any) =>
            String(variant).toLowerCase().trim()
          )
        );
      }

      const userVal = String(userAnswer).toLowerCase().trim();
      const isCorrect = accepted.includes(userVal);

      return isCorrect;
    }

    // Generic fallback for other question types
    if (question.correct && userAnswer) {
      const correctAnswer = Array.isArray(question.correct)
        ? question.correct
        : [question.correct];
      const userAnswerArray = Array.isArray(userAnswer)
        ? userAnswer
        : [userAnswer];

      // Normalize answers for comparison (trim whitespace, lowercase)
      const normalizedCorrect = correctAnswer.map((ans: any) =>
        String(ans).trim().toLowerCase()
      );
      const normalizedUser = userAnswerArray.map((ans: any) =>
        String(ans).trim().toLowerCase()
      );

      // Check if any user answer matches any correct answer
      const isCorrect = normalizedUser.some((userAns: string) =>
        normalizedCorrect.includes(userAns)
      );

      return isCorrect;
    }

    // If we can't determine, default to false
    return false;
  };

  const loadAvailableGrades = async () => {
    try {
      if (!submission?.student_id) {
        setAvailableGrades(["A", "B", "C", "D", "F"]);
        return;
      }

      // Get the student's class and its grade type
      const { data: classStudent, error: classStudentError } = await supabase
        .from("class_students")
        .select("class_id")
        .eq("user_id", submission.student_id)
        .maybeSingle();

      if (classStudentError || !classStudent) {
        console.error("Error fetching student class:", classStudentError);
        setAvailableGrades(["A", "B", "C", "D", "F"]);
        return;
      }

      // Get the class details including grade_type
      const { data: classData, error: classError } = await supabase
        .from("classes")
        .select("grade_type")
        .eq("id", classStudent.class_id)
        .single();

      if (classError || !classData) {
        console.error("Error fetching class data:", classError);
        setAvailableGrades(["A", "B", "C", "D", "F"]);
        return;
      }

      const gradeType = classData.grade_type;

      // First check if it's a predefined scale
      const predefinedScale = PREDEFINED_SCALES.find(
        (scale) => scale.name === gradeType
      );

      if (predefinedScale && predefinedScale.values) {
        // Use predefined scale
        const grades = predefinedScale.values.map((value) => value.label);
        setAvailableGrades(grades);
        return;
      }

      // If not predefined, check the grade_scales table
      const { data: gradeScales } = await supabase
        .from("grade_scales")
        .select("*")
        .eq("name", gradeType)
        .single();

      if (gradeScales && gradeScales.values) {
        // Handle array structure for custom grade scales
        if (Array.isArray(gradeScales.values)) {
          const grades = gradeScales.values.map((value: any) => value.label);
          setAvailableGrades(grades);
        } else {
          // Handle object structure (fallback)
          const grades = Object.keys(gradeScales.values);
          setAvailableGrades(grades);
        }
      } else {
        // Fallback to standard grades
        setAvailableGrades(["A", "B", "C", "D", "F"]);
      }
    } catch (error) {
      console.error("Error loading available grades:", error);
      setAvailableGrades(["A", "B", "C", "D", "F"]);
    }
  };

  const handleGradeChange = async (newGrade: string) => {
    setSelectedGrade(newGrade);

    if (!submission?.student_id) {
      setGradePercentage(0);
      return;
    }

    // Get the student's class and its grade type
    const { data: classStudent, error: classStudentError } = await supabase
      .from("class_students")
      .select("class_id")
      .eq("user_id", submission.student_id)
      .maybeSingle();

    if (classStudentError || !classStudent) {
      console.error("Error fetching student class:", classStudentError);
      setGradePercentage(0);
      return;
    }

    // Get the class details including grade_type
    const { data: classData, error: classError } = await supabase
      .from("classes")
      .select("grade_type")
      .eq("id", classStudent.class_id)
      .single();

    if (classError || !classData) {
      console.error("Error fetching class data:", classError);
      setGradePercentage(0);
      return;
    }

    const gradeType = classData.grade_type;
    let gradePercentage = 0;

    // First check if it's a predefined scale
    const predefinedScale = PREDEFINED_SCALES.find(
      (scale) => scale.name === gradeType
    );

    if (predefinedScale && predefinedScale.values) {
      // Find the grade in predefined scale
      const gradeValue = predefinedScale.values.find(
        (value) => value.label === newGrade
      );
      if (gradeValue) {
        gradePercentage = gradeValue.default;
      }
    } else {
      // Check custom grade scales
      try {
        const { data: gradeScales } = await supabase
          .from("grade_scales")
          .select("*")
          .eq("name", gradeType)
          .single();

        if (gradeScales && gradeScales.values) {
          // Handle array structure for custom grade scales
          if (Array.isArray(gradeScales.values)) {
            const gradeValue = gradeScales.values.find(
              (value: any) => value.label === newGrade
            );
            if (gradeValue) {
              gradePercentage = gradeValue.default;
            }
          } else {
            // Handle object structure (fallback)
            if (gradeScales.values[newGrade]) {
              gradePercentage = gradeScales.values[newGrade];
            }
          }
        }
      } catch (error) {
        console.error("Error fetching grade scale:", error);
      }
    }

    // Fallback to standard percentages if no scale found
    if (gradePercentage === 0) {
      const standardPercentages: { [key: string]: number } = {
        A: 90,
        "A-": 87,
        "B+": 83,
        B: 80,
        "B-": 77,
        "C+": 73,
        C: 70,
        "C-": 67,
        "D+": 63,
        D: 60,
        "D-": 57,
        F: 50,
      };
      gradePercentage = standardPercentages[newGrade] || 0;
    }

    setGradePercentage(gradePercentage);
  };

  const handleSaveGrade = async () => {
    if (!submission) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("student_assignments")
        .update({
          grade: selectedGrade,
          grade_percentage: gradePercentage,
        })
        .eq("id", submission.id);

      if (error) {
        throw error;
      }

      toast.success("Grade updated successfully");

      setSubmission((prev) =>
        prev
          ? {
              ...prev,
              grade: selectedGrade,
              grade_percentage: gradePercentage,
            }
          : null
      );
    } catch (error) {
      console.error("Error saving grade:", error);
      toast.error("Failed to save grade");
    } finally {
      setSaving(false);
    }
  };

  const handleSendFeedback = async () => {
    if (!submission || !teacherFeedback.trim()) return;

    setSendingFeedback(true);
    try {
      const { error } = await supabase
        .from("student_assignments")
        .update({
          teacher_feedback: teacherFeedback.trim(),
        })
        .eq("id", submission.id);

      if (error) {
        throw error;
      }

      toast.success("Feedback sent successfully");

      // Update local state
      setSubmission((prev) =>
        prev
          ? {
              ...prev,
              teacher_feedback: teacherFeedback.trim(),
            }
          : null
      );
    } catch (error) {
      console.error("Error sending feedback:", error);
      toast.error("Failed to send feedback");
    } finally {
      setSendingFeedback(false);
    }
  };

  const renderQuestion = (
    question: any,
    index: number,
    sectionIndex?: number
  ) => {
    const answerKey =
      sectionIndex !== undefined ? `${sectionIndex}-${index}` : index;
    const userAnswer = answers[answerKey];
    const evaluation = evaluations[answerKey];

    const isCorrect = checkAnswerCorrectness(question, userAnswer, evaluation);
    const feedback = evaluation?.feedback || "";

    return (
      <div key={answerKey} className="bg-white rounded-lg border p-4 mb-4">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">
            Question{" "}
            {sectionIndex !== undefined
              ? `${sectionIndex + 1}.${index + 1}`
              : index + 1}
          </h3>
          <div className="flex items-center">
            {isCorrect ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
          </div>
        </div>

        <div className="mb-3">
          {question.type === "FB" ? (
            <p className="text-gray-700">{question.prompt}</p>
          ) : (
            <p className="text-gray-700">{question.question}</p>
          )}
        </div>

        {question.type === "MC" && question.options && (
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-600 mb-2">Options:</p>
            <div className="space-y-1">
              {question.options.map((option: string, optIndex: number) => (
                <div key={optIndex} className="text-sm text-gray-600">
                  {String.fromCharCode(65 + optIndex)}. {option}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-3">
          <p className="text-sm font-medium text-gray-600 mb-1">
            Student Answer:
          </p>
          <p className="text-gray-900 bg-gray-50 p-2 rounded">
            {Array.isArray(userAnswer)
              ? userAnswer.join(", ")
              : userAnswer || "No answer provided"}
          </p>
        </div>

        {question.correct && (
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-600 mb-1">
              Correct Answer:
            </p>
            <p className="text-green-700 bg-green-50 p-2 rounded">
              {Array.isArray(question.correct)
                ? question.correct.join(", ")
                : question.correct}
            </p>
          </div>
        )}

        {question.accept_also &&
          question.type === "FB" &&
          Array.isArray(question.accept_also) && (
            <div className="mt-1 text-sm text-gray-600">
              <strong>Also accepted:</strong>
              <ul className="list-disc list-inside ml-4 mt-1">
                {question.accept_also.map((variants, idx) => {
                  if (Array.isArray(variants)) {
                    return (
                      <li key={idx}>
                        Blank {idx + 1}: {variants.join(" / ")}
                      </li>
                    );
                  } else if (typeof variants === "string") {
                    return (
                      <li key={idx}>
                        Blank {idx + 1}: {variants}
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            </div>
          )}

        {feedback && (
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-600 mb-1">
              AI Feedback:
            </p>
            <p className="text-blue-700 bg-blue-50 p-2 rounded">{feedback}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading submission...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Submission Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The submission you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <Link
            to={
              role === "teacher"
                ? "/teacher/dashboard?tab=assignments"
                : role === "admin"
                ? "/admin/dashboard"
                : "/superadmin/dashboard"
            }
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          {ltiToken === "" ||
            (ltiToken === null && (
              <Link
                to={
                  role === "teacher"
                    ? "/teacher/dashboard?tab=assignments"
                    : role === "admin"
                    ? "/admin/dashboard"
                    : "/superadmin/dashboard"
                }
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900 mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            ))}

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {submission.assignment.assessment_name}
                </h1>
                <p className="text-gray-600 mb-1">
                  Student:{" "}
                  <span className="font-medium">
                    {submission.student.fullname}
                  </span>
                </p>
                <p className="text-gray-600 mb-1">
                  Type:{" "}
                  <span className="font-medium capitalize">
                    {submission.assignment.type}
                  </span>
                </p>
                <p className="text-gray-600">
                  Submitted:{" "}
                  <span className="font-medium">
                    {new Date(submission.submitted_at).toLocaleDateString()}
                  </span>
                </p>
              </div>

              <div className="text-right">
                <div className="text-sm text-gray-500 mb-1">AI Grade</div>
                <div className="text-2xl font-bold text-indigo-600">
                  {submission.grade || "N/A"}
                </div>
                <div className="text-sm text-gray-500">
                  {submission.grade_percentage}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Questions */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Assessment Results
                </h2>
              </div>

              <div className="p-6">
                {/* Overview */}
                {overview && (
                  <div className="mb-6 bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Overview
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {overview.total}
                        </div>
                        <div className="text-sm text-gray-500">
                          Total Questions
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {overview.correct}
                        </div>
                        <div className="text-sm text-gray-500">Correct</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {overview.incorrect}
                        </div>
                        <div className="text-sm text-gray-500">Incorrect</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-indigo-600">
                          {overview.percent}%
                        </div>
                        <div className="text-sm text-gray-500">Score</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Questions */}
                {assessment && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Questions
                    </h3>
                    {assessment.sections
                      ? // Sectioned assessment
                        assessment.sections.map(
                          (section: any, sectionIndex: number) => (
                            <div key={sectionIndex} className="mb-6">
                              <h4 className="text-md font-medium text-gray-700 mb-3">
                                Section {sectionIndex + 1}: {section.title}
                              </h4>
                              {section.questions.map(
                                (question: any, index: number) =>
                                  renderQuestion(question, index, sectionIndex)
                              )}
                            </div>
                          )
                        )
                      : // Flat assessment
                        assessment.questions.map(
                          (question: any, index: number) =>
                            renderQuestion(question, index)
                        )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Grade Adjustment & Feedback */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Adjust Grade
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Grade
                    </label>
                    <select
                      value={selectedGrade}
                      onChange={(e) => handleGradeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select a grade</option>
                      {availableGrades.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grade Percentage
                    </label>
                    <input
                      type="number"
                      value={gradePercentage}
                      onChange={(e) =>
                        setGradePercentage(Number(e.target.value))
                      }
                      min="0"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <button
                    onClick={handleSaveGrade}
                    disabled={saving || !selectedGrade || role !== "teacher"}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Saving..." : "Save Grade"}
                  </button>
                </div>
              </div>

              {/* Teacher Feedback */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Send Feedback
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Feedback Message
                    </label>
                    <textarea
                      value={teacherFeedback}
                      onChange={(e) => setTeacherFeedback(e.target.value)}
                      rows={6}
                      placeholder="Provide constructive feedback to the student..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <button
                    onClick={handleSendFeedback}
                    disabled={
                      sendingFeedback ||
                      !teacherFeedback.trim() ||
                      role !== "teacher"
                    }
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sendingFeedback ? "Sending..." : "Send Feedback"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionReviewPage;
